import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Modal, Form, Alert, Badge } from 'react-bootstrap';
import { userApi } from '../services/api';
import { FiUsers, FiPlus, FiEdit, FiTrash2, FiShield, FiUser } from 'react-icons/fi';
import { useAuth } from '../hooks/useAuth';

const UsersPage = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    role: 'user'
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await userApi.getAll();
      setUsers(response.data.data.users);
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (userToEdit = null) => {
    if (userToEdit) {
      setEditingUser(userToEdit);
      setFormData({
        email: userToEdit.email,
        password: '',
        role: userToEdit.role
      });
    } else {
      setEditingUser(null);
      setFormData({
        email: '',
        password: '',
        role: 'user'
      });
    }
    setShowModal(true);
    setError('');
    setSuccess('');
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingUser(null);
    setFormData({
      email: '',
      password: '',
      role: 'user'
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      if (editingUser) {
        // Update user
        const updateData = {
          email: formData.email,
          role: formData.role
        };
        if (formData.password) {
          updateData.password = formData.password;
        }
        await userApi.update(editingUser.id, updateData);
        setSuccess('User updated successfully!');
      } else {
        // Create user
        if (!formData.password) {
          setError('Password is required for new users');
          return;
        }
        await userApi.create(formData);
        setSuccess('User created successfully!');
      }
      handleCloseModal();
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save user');
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) {
      return;
    }

    try {
      await userApi.delete(userId);
      setSuccess('User deleted successfully!');
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete user');
    }
  };

  const getRoleBadge = (role) => {
    const badges = {
      business: { variant: 'primary', label: 'Owner', icon: <FiShield /> },
      business_admin: { variant: 'success', label: 'Admin', icon: <FiShield /> },
      user: { variant: 'secondary', label: 'User', icon: <FiUser /> }
    };
    const badge = badges[role] || { variant: 'secondary', label: role, icon: <FiUser /> };
    return (
      <Badge bg={badge.variant} className="d-flex align-items-center gap-1" style={{ width: 'fit-content' }}>
        {badge.icon}
        {badge.label}
      </Badge>
    );
  };

  const isOwner = user?.role === 'business';
  const isAdmin = user?.role === 'business_admin' || isOwner;

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="loading-spinner" style={{ margin: '0 auto', width: '40px', height: '40px' }}></div>
        <p className="mt-3">Loading users...</p>
      </div>
    );
  }

  return (
    <div className="users-page">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>
          <FiUsers className="me-2" />
          Company Users
        </h2>
        {isAdmin && (
          <Button variant="primary" onClick={() => handleOpenModal()}>
            <FiPlus className="me-2" />
            Add User
          </Button>
        )}
      </div>

      {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert variant="success" dismissible onClose={() => setSuccess('')}>{success}</Alert>}

      <Card>
        <Card.Body>
          {users.length === 0 ? (
            <div className="text-center py-5">
              <FiUsers size={48} className="text-muted mb-3" />
              <p className="text-muted">No users found</p>
              {isAdmin && (
                <Button variant="primary" onClick={() => handleOpenModal()}>
                  <FiPlus className="me-2" />
                  Add First User
                </Button>
              )}
            </div>
          ) : (
            <Table responsive hover>
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Created</th>
                  {isAdmin && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id}>
                    <td>{u.email}</td>
                    <td>{getRoleBadge(u.role)}</td>
                    <td>{new Date(u.createdAt).toLocaleDateString()}</td>
                    {isAdmin && (
                      <td>
                        <div className="d-flex gap-2">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleOpenModal(u)}
                            disabled={u.role === 'business'}
                            title={u.role === 'business' ? 'Cannot edit owner account' : 'Edit user'}
                          >
                            <FiEdit />
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDelete(u.id)}
                            disabled={u.id === user?.id || u.role === 'business'}
                            title={u.id === user?.id ? 'Cannot delete your own account' : u.role === 'business' ? 'Cannot delete owner account' : 'Delete user'}
                          >
                            <FiTrash2 />
                          </Button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Create/Edit User Modal */}
      <Modal show={showModal} onHide={handleCloseModal}>
        <Modal.Header closeButton>
          <Modal.Title>
            {editingUser ? 'Edit User' : 'Add New User'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            {error && <Alert variant="danger">{error}</Alert>}
            
            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                Password {editingUser && <span className="text-muted">(leave blank to keep current)</span>}
              </Form.Label>
              <Form.Control
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required={!editingUser}
                minLength={8}
              />
              <Form.Text className="text-muted">
                Password must be at least 8 characters long
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Role</Form.Label>
              <Form.Select
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                required
              >
                <option value="user">User - Can create appointments</option>
                <option value="business_admin">Admin - Can manage users and settings</option>
              </Form.Select>
              <Form.Text className="text-muted">
                Users can create appointments. Admins can also manage users and business settings.
              </Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              {editingUser ? 'Update User' : 'Create User'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default UsersPage;
